// Shared metadata block for Shipments CSV exports (Planning Console + Pivot).
// Both views lead their export with the same "Parameter,Value" preamble so a
// downloaded file is self-documenting and the two exports read identically.
//
// The Pivot builds the CSV client-side and prepends these rows directly; the
// Planning Console passes them to its backend export transform
// (ui_supply_walk_for_editing_export), which prepends them the same way.

export interface ExportMetaParams {
  view: "Planning Console" | "Pivot";
  runLabel: string; // e.g. "06/01/2026 9:48 PM — Run for lmnt"
  exportedAt: string; // ISO date
  mode: "collapsed" | "expanded";
  grain: string; // PC: "Planning Level × SKU"; Pivot: chosen segment labels
  measuresShown: string; // collapsed: the one measure; expanded: "Full supply walk"
  unitsLabel: string; // e.g. "Sticks"
  displayBy: string; // PC: "Week"; Pivot: the period label
  dateRange: string; // e.g. "Jun '26 – Mar '28"
  showActuals: boolean;
  sortLabel: string; // active sort, or "(default order)"
  filtersLabel: string; // human filter summary, "(none)" when empty
}

/** The ordered [key, value] rows of the export preamble. Row 0 is the title. */
export function buildExportMetaRows(p: ExportMetaParams): [string, string][] {
  return [
    [`Lmnt Shipments — ${p.view} export`, ""],
    ["Run", p.runLabel || "(current run)"],
    ["Exported", p.exportedAt],
    ["Mode", p.mode === "collapsed" ? "Collapsed" : "Expanded"],
    ["Grain", p.grain],
    [p.mode === "collapsed" ? "Measure shown" : "Measures shown", p.measuresShown],
    ["Units", p.unitsLabel],
    ["Display by", p.displayBy],
    ["Date range", p.dateRange || "(all)"],
    ["Show actuals", p.showActuals ? "On" : "Off"],
    ["Sort", p.sortLabel || "(default order)"],
    ["Filters", p.filtersLabel || "(none)"],
  ];
}

/** Render sort criteria as a compact human label, e.g. "Jun '26 ↓, First Receiver ↑". */
export function sortLabel(
  criteria: { column: string; direction: "asc" | "desc" }[],
  labelFor: (col: string) => string,
): string {
  if (!criteria.length) return "(default order)";
  return criteria
    .map((c) => `${labelFor(c.column)} ${c.direction === "asc" ? "↑" : "↓"}`)
    .join(", ");
}
